import { Component, Injectable, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { OpeningSystemType } from 'src/app/enum/closet-doors-custom/opening-system-type.enum';
import { DoorRange } from 'src/app/models/configurators-2d/applications/closet-doors-custom/door-range';
import { SupplierDoorRange } from 'src/app/models/configurators-2d/applications/closet-doors-custom/supplier-door-range';
import { ClosetDoorsCustomService } from 'src/app/services/configurators-2d/applications/closet-doors-custom.service';
import { ClosetDoorsCustomConfiguratorItem } from '../../../configurator/applications/closet-doors-custom-configurator-item';
import { Configurators2DComponent } from '../../../configurators-2d.component';
import { IBaseComponent } from '../../../configurator/i-base-component';
import { ClosetDoorsCustomPreviewComponent } from '../preview/preview.component';

@Component({
  selector: 'ng-closet-doors-custom-suppliers',
  templateUrl: './suppliers.component.html',
})
@Injectable({ providedIn: 'root' })
export class ClosetDoorsCustomSuppliersComponent
  extends ClosetDoorsCustomConfiguratorItem
  implements OnInit, IBaseComponent
{
  doorRangesGroup: SupplierDoorRange[];
  doorRanges: DoorRange[];

  currentLang;
  baseImgURL;
  preview: ClosetDoorsCustomPreviewComponent;

  constructor(
    private translate: TranslateService,
    closetDoorsCustomService: ClosetDoorsCustomService,
    configurators2DComponent: Configurators2DComponent
  ) {
    super(closetDoorsCustomService);
    this.baseImgURL = this.service.apiUrl;
    this.currentLang = this.translate.getBrowserLang();
    this.preview = configurators2DComponent.preview as ClosetDoorsCustomPreviewComponent;
  }

  ngOnInit(): void {
    this.service.getDoorRangesGroupBySupplier().then(doorRangesGroup => {
      this.doorRangesGroup = doorRangesGroup;
      if (this.doorRangesGroup?.length > 0) {
        if (!this.configuration.supplier && !this.configuration.doorRange) {
          this.selectSupplier(this.doorRangesGroup.find(_ => _));
          this.selectDoorRange(this.doorRangesGroup.find(_ => _).doorRanges.find(_ => _));
        } else {
          //ajouter la doorrange disabled si elle est sélectionnée dans la configuration
          if (this.configuration.doorRange.isDisabled) {
            this.doorRangesGroup
              .find(d => d.supplier.id === this.configuration.supplier.id)
              .doorRanges.push(this.configuration.doorRange);
          }

          if (
            !this.doorRangesGroup
              .find(g => g.supplier.id === this.configuration.supplier.id)
              .doorRanges.find(r => r.id === this.configuration.doorRange.id)
          ) {
            this.configuration.doorRange.isDisabled = true;
            this.doorRangesGroup
              .find(d => d.supplier.id === this.configuration.supplier.id)
              .doorRanges.push(this.configuration.doorRange);
          }

          this.selectSupplier(this.doorRangesGroup.find(g => g.supplier.id === this.configuration.supplier.id));
          this.selectDoorRange(
            this.doorRangesGroup
              .find(g => g.supplier.id === this.configuration.supplier.id)
              .doorRanges.find(r => r.id === this.configuration.doorRange.id)
          );
        }
      }
    });
  }

  /**
   * Sélection d'un fournisseur
   * @param group SupplierDoorRange
   */
  selectSupplier(group: SupplierDoorRange): void {
    //Reset des objets si le supplier change
    if (this.configuration.supplier?.id !== group.supplier.id) {
      this.configuration.doorRange = undefined;
      this.configuration.doorStructure = undefined;
      this.configuration.configurationDoorStructures = undefined;
      this.configuration.frame = undefined;
      this.configuration.frameFinish = undefined;
      this.configuration.configurationAccessories = undefined;
      this.configuration.resume = undefined;
      this.selectDoorRange(group.doorRanges.find(() => true));
    }

    this.configuration.supplier = group.supplier;
    this.doorRanges = group.doorRanges;
  }

  /**
   * Détermination si le fournisseur est sélectionné
   * @param id id du fournisseur
   * @returns boolean
   */
  isSupplierSelected(id: number): boolean {
    return this.configuration.supplier?.id == id;
  }

  /**
   * Détermination si un fournisseur a été sélectionné
   * @returns boolean
   */
  configurationHasSupplier(): boolean {
    return this.configuration?.supplier != null;
  }

  /**
   * Sélection d'une gamme
   * @param doorRange
   */
  selectDoorRange(doorRange: DoorRange): void {
    //Reset des objets si le supplier change
    if (this.configuration.doorRange?.id !== doorRange.id) {
      this.configuration.doorStructure = undefined;
      this.configuration.configurationDoorStructures = undefined;
      this.configuration.frame = undefined;
      this.configuration.frameFinish = undefined;
      this.configuration.configurationAccessories = undefined;
      this.configuration.resume = undefined;
    }

    this.configuration.doorRange = doorRange;
    this.preview.getBackgroundImage();
    this.updateHasDisabledProduct();
  }

  /**
   * Détermination si la gamme est désactivée
   * @returns boolean
   */
  isDoorRangeValid(): boolean {
    return this.configuration.doorRange ? !this.configuration.doorRange?.isDisabled : true;
  }

  /**
   * Détermination si la gamme est sélectionnée
   * @param id id de la gamme
   * @returns boolean
   */
  isDoorRangeSelected(id: number): boolean {
    return this.configuration.doorRange?.id == id;
  }

  isNextButtonActive(): boolean {
    return (
      this.configuration.supplier != null &&
      this.configuration.doorRange != null &&
      !this.configuration.doorRange.isDisabled
    );
  }

  checkPageChangeCapability(): boolean {
    let clientHasSize =
      this.configuration.clientHeight != 0 &&
      this.configuration.clientHeight != null &&
      this.configuration.clientWidth != 0 &&
      this.configuration.clientWidth != null;
    let clientHasOpening = this.configuration.openingSystem != null;
    let clientHasPanels = this.configuration.nbDoor != null && this.configuration.nbDoor != 0;
    let openingPercentOrOpeningSystemSide =
      this.configuration.openingSystem?.type == OpeningSystemType.sliding
        ? this.configuration.percentageOpening != null && this.configuration.percentageOpening != 0
        : this.configuration.openingSystemSide != null;
    return clientHasSize && clientHasOpening && clientHasPanels && openingPercentOrOpeningSystemSide;
  }

  updateHasDisabledProduct(): void {
    if (this.configuration.hasDisabledProducts) {
      let hasDisabledProduct: boolean = false;

      if (this.configuration.doorRange) {
        if (this.configuration.doorRange.isDisabled) {
          hasDisabledProduct = true;
        }
      }

      if (this.configuration.configurationDoorStructures) {
        if (
          this.configuration.frame.isDisabled ||
          this.configuration.frameFinish.isDisabled ||
          this.configuration.configurationDoorStructures.find(cd => cd.doorStructure.isDisabled) != null ||
          this.configuration.configurationDoorStructures.find(
            cd =>
              cd.configurationDoorSections.find(ds => ds.color.isDisabled) ||
              this.configuration.configurationDoorStructures.find(cd =>
                cd.configurationDoorSections.find(ds =>
                  ds.doorSection.colorCategories.find(cc => cc.id === ds.color.colorCategoryId && cc.isDisabled)
                )
              )
          )
        ) {
          hasDisabledProduct = true;
        }
      }

      if (this.configuration.configurationAccessories) {
        if (this.configuration.configurationAccessories.find(ca => ca.accessory.isDisabled)) {
          hasDisabledProduct = true;
        }
      }

      this.configuration.hasDisabledProducts = hasDisabledProduct;
    }
  }
}
